// # Revenue Analytics Component
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Area
} from 'recharts';
import './bnct_revenue_analytics.css';

// # Chart colors
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const RevenueAnalytics = ({ revenueData, member }) => {
  // # State for time period filtering
  const [timePeriod, setTimePeriod] = useState('all');
  const [filteredData, setFilteredData] = useState({});
  
  // # Determine if this is a project budget account
  const isProjectBudget = member && member.id === 'project-budget';
  
  // # Process and filter data based on time period
  useEffect(() => {
    if (!revenueData) return;
    
    // # Get current date for filtering
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // # Create timeline data from revenueOverTime - use stored values directly
    const timelineData = [];
    
    // # Process revenueOverTime to create chart data
    if (revenueData.revenueOverTime) {
      // # Sort months chronologically
      const sortedMonths = Object.keys(revenueData.revenueOverTime).sort();
      
      // # Process each month's data
      sortedMonths.forEach(month => {
        const [yearStr, monthStr] = month.split('-');
        const monthDate = new Date(parseInt(yearStr), parseInt(monthStr) - 1);
        const monthData = revenueData.revenueOverTime[month];
        
        // # Apply time period filter
        let includeMonth = true;
        if (timePeriod === '3m') {
          // # Last 3 months
          const threeMonthsAgo = new Date(currentYear, currentMonth - 3);
          includeMonth = monthDate >= threeMonthsAgo;
        } else if (timePeriod === '6m') {
          // # Last 6 months
          const sixMonthsAgo = new Date(currentYear, currentMonth - 6);
          includeMonth = monthDate >= sixMonthsAgo;
        } else if (timePeriod === '1y') {
          // # Last year
          const oneYearAgo = new Date(currentYear - 1, currentMonth);
          includeMonth = monthDate >= oneYearAgo;
        }
        
        if (includeMonth) {
          // # For project budget account
          if (isProjectBudget) {
            timelineData.push({
              month,
              budget: monthData.budget || 0,
              expenses: monthData.expenses || 0,
              net: monthData.net || 0
            });
          } 
          // # For team member accounts
          else {
            timelineData.push({
              month,
              revenue: monthData.revenue || 0,
              salaries: monthData.salaries || 0,
              net: monthData.net || 0
            });
          }
        }
      });
    }
    
    // # Calculate total revenue for the filtered period
    const totalRevenue = timelineData.reduce((sum, data) => 
      sum + (isProjectBudget ? data.budget : data.revenue), 0);
    
    // # Calculate total net revenue for the filtered period
    const totalNetRevenue = timelineData.reduce((sum, data) => 
      sum + (data.net || 0), 0);
    
    // # Calculate total salaries/expenses for the filtered period
    const totalDeductions = timelineData.reduce((sum, data) => 
      sum + (isProjectBudget ? (data.expenses || 0) : (data.salaries || 0)), 0);
    
    // # Calculate performance metrics
    const performanceMetrics = calculatePerformanceMetrics(
      timelineData, 
      isProjectBudget,
      totalRevenue,
      totalNetRevenue,
      totalDeductions
    );
    
    // # Set filtered data
    setFilteredData({
      timelineData,
      totalRevenue,
      totalNetRevenue,
      totalDeductions,
      performanceMetrics,
      // # Use stored product data directly
      productData: isProjectBudget ? 
        (revenueData.budgetByProduct || {}) : 
        (revenueData.revenueByProduct || {})
    });
  }, [revenueData, timePeriod, isProjectBudget]);
  
  // # If no data is available
  if (!revenueData) return <div className="bnct_loading">Loading analytics data...</div>;
  
  return (
    <div className="bnct_analytics-container">
      {/* # Time Period Filter */}
      <div className="bnct_analytics-filters">
        <h3>Time Period</h3>
        <div className="bnct_analytics-filter-btn-group">
          <button 
            className={`bnct_analytics-filter-btn ${timePeriod === 'all' ? 'active' : ''}`}
            onClick={() => setTimePeriod('all')}
          >
            All Time
          </button>
          <button 
            className={`bnct_analytics-filter-btn ${timePeriod === '1y' ? 'active' : ''}`}
            onClick={() => setTimePeriod('1y')}
          >
            Last Year
          </button>
          <button 
            className={`bnct_analytics-filter-btn ${timePeriod === '6m' ? 'active' : ''}`}
            onClick={() => setTimePeriod('6m')}
          >
            Last 6 Months
          </button>
          <button 
            className={`bnct_analytics-filter-btn ${timePeriod === '3m' ? 'active' : ''}`}
            onClick={() => setTimePeriod('3m')}
          >
            Last 3 Months
          </button>
        </div>
      </div>
      
      {/* # Performance Metrics */}
      {renderPerformanceMetrics()}
      
      {/* # Product Analysis */}
      {renderProductAnalysis()}
      
      {/* # Revenue Trend Analysis */}
      <div className="bnct_analytics-section">
        <h3>{isProjectBudget ? 'Budget Trend Analysis' : 'Revenue Trend Analysis'}</h3>
        <div className="bnct_analytics-chart-wrapper">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filteredData.timelineData || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tickFormatter={(month) => {
                  const [year, monthNum] = month.split('-');
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
                }}
              />
              <YAxis />
              <Tooltip formatter={(value) => `E£ ${formatCurrency(value)}`} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={isProjectBudget ? "budget" : "revenue"} 
                stroke="#8884d8" 
                name={isProjectBudget ? "Budget Allocation" : "Net Revenue"} 
                activeDot={{ r: 8 }} 
              />
              <Line 
                type="monotone" 
                dataKey={isProjectBudget ? "expenses" : "salaries"} 
                stroke="#82ca9d" 
                name={isProjectBudget ? "Expenses" : "Salaries"} 
                activeDot={{ r: 8 }} 
              />
              <Line 
                type="monotone" 
                dataKey="net" 
                stroke="#ffc658" 
                name={isProjectBudget ? "Net Budget" : "Available Cash"} 
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* # Revenue vs Net Revenue Analysis */}
      <div className="bnct_analytics-section">
        <h3>{isProjectBudget ? 'Budget vs Expenses Analysis' : 'Revenue vs Salaries Analysis'}</h3>
        <div className="bnct_analytics-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={filteredData.timelineData || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tickFormatter={(month) => {
                  const [year, monthNum] = month.split('-');
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
                }}
              />
              <YAxis />
              <Tooltip formatter={(value) => `E£ ${formatCurrency(value)}`} />
              <Legend />
              <Bar 
                dataKey={isProjectBudget ? "budget" : "revenue"} 
                fill="#8884d8" 
                name={isProjectBudget ? "Budget Allocation" : "Net Revenue"} 
              />
              <Bar 
                dataKey={isProjectBudget ? "expenses" : "salaries"} 
                fill="#82ca9d" 
                name={isProjectBudget ? "Expenses" : "Salaries"} 
              />
              <Line 
                type="monotone" 
                dataKey="net" 
                stroke="#ff7300" 
                name={isProjectBudget ? "Net Budget" : "Available Cash"} 
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
  
  // # Handle period change
  function handlePeriodChange(period) {
    setTimePeriod(period);
  }
  
  // # Render performance metrics section
  function renderPerformanceMetrics() {
    if (!filteredData.performanceMetrics) return null;
    
    const metrics = filteredData.performanceMetrics;
    
    return (
      <div className="bnct_analytics-section">
        <h3>Performance Metrics</h3>
        <div className="bnct_analytics-metrics-grid">
          {/* # Total Revenue */}
          <div className="bnct_analytics-metric-card">
            <h4>{isProjectBudget ? 'Total Budget Allocation' : 'Net Revenue'}</h4>
            <p className="bnct_analytics-metric-value">E£ {formatCurrency(filteredData.totalRevenue)}</p>
            <p className="bnct_analytics-metric-desc">
              {timePeriod === 'all' ? 'All time' : 
               timePeriod === '1y' ? 'Last 12 months' : 
               timePeriod === '6m' ? 'Last 6 months' : 'Last 3 months'}
            </p>
          </div>
          
          {/* # Total Deductions */}
          <div className="bnct_analytics-metric-card">
            <h4>{isProjectBudget ? 'Total Expenses' : 'Total Salaries'}</h4>
            <p className="bnct_analytics-metric-value">E£ {formatCurrency(filteredData.totalDeductions)}</p>
            <p className="bnct_analytics-metric-desc">
              Total {isProjectBudget ? 'expenses' : 'salaries'} for the period
            </p>
          </div>
          
          {/* # Net Revenue */}
          <div className="bnct_analytics-metric-card">
            <h4>{isProjectBudget ? 'Net Budget' : 'Available Cash'}</h4>
            <p className="bnct_analytics-metric-value">E£ {formatCurrency(filteredData.totalNetRevenue)}</p>
            <p className="bnct_analytics-metric-desc">
              {isProjectBudget ? 'Budget' : 'Revenue'} after {isProjectBudget ? 'expenses' : 'salaries'}
            </p>
          </div>
          
          {/* # Monthly Average */}
          <div className="bnct_analytics-metric-card">
            <h4>Monthly Average</h4>
            <p className="bnct_analytics-metric-value">E£ {formatCurrency(metrics.monthlyAverage)}</p>
            <p className="bnct_analytics-metric-desc">
              Average {isProjectBudget ? 'budget' : 'revenue'} per month
            </p>
          </div>
          
          {/* # Growth Rate */}
          <div className="bnct_analytics-metric-card">
            <h4>Growth Rate</h4>
            <p className={`bnct_analytics-metric-value ${metrics.growthRate >= 0 ? 'positive' : 'negative'}`}>
              {metrics.growthRate >= 0 ? '+' : ''}{formatPercentage(metrics.growthRate)}%
            </p>
            <p className="bnct_analytics-metric-desc">
              From first to last month in period
            </p>
          </div>
          
          {/* # Volatility */}
          <div className="bnct_analytics-metric-card">
            <h4>Volatility</h4>
            <p className="bnct_analytics-metric-value">{formatPercentage(metrics.volatility)}%</p>
            <p className="bnct_analytics-metric-desc">
              Month-to-month variation
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // # Render product analysis section
  function renderProductAnalysis() {
    if (!filteredData.productData) return null;
    
    // # Convert product data to array for charts
    const productChartData = Object.entries(filteredData.productData)
      .map(([product, amount]) => ({
        product,
        amount
      }))
      .sort((a, b) => b.amount - a.amount);
    
    return (
      <div className="bnct_analytics-section">
        <h3>{isProjectBudget ? 'Budget Allocation by Product' : 'Net Revenue by Product'}</h3>
        <div className="bnct_analytics-charts">
          {/* # Bar Chart */}
          <div className="bnct_analytics-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product" />
                <YAxis />
                <Tooltip formatter={(value) => `E£ ${formatCurrency(value)}`} />
                <Legend />
                <Bar 
                  dataKey="amount" 
                  fill="#8884d8" 
                  name={isProjectBudget ? "Budget (EGP)" : "Revenue (EGP)"} 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* # Pie Chart */}
          <div className="bnct_analytics-chart">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={productChartData}
                  dataKey="amount"
                  nameKey="product"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ product, percent }) => `${product}: ${(percent * 100).toFixed(0)}%`}
                >
                  {productChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `E£ ${formatCurrency(value)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }
};

// # Helper function to calculate performance metrics
const calculatePerformanceMetrics = (timelineData, isProjectBudget, totalRevenue, totalNetRevenue, totalDeductions) => {
  if (!timelineData || timelineData.length === 0) {
    return {
      monthlyAverage: 0,
      growthRate: 0,
      volatility: 0
    };
  }
  
  // # Extract revenue values
  const values = timelineData.map(data => 
    isProjectBudget ? data.budget : data.revenue
  );
  
  // # Calculate monthly average
  const monthlyAverage = values.reduce((sum, value) => sum + (value || 0), 0) / values.length;
  
  // # Calculate growth rate (if we have at least 2 months of data)
  let growthRate = 0;
  if (values.length >= 2) {
    const firstMonth = values[0] || 0;
    const lastMonth = values[values.length - 1] || 0;
    
    if (firstMonth > 0) {
      growthRate = ((lastMonth - firstMonth) / firstMonth) * 100;
    }
  }
  
  // # Calculate volatility
  const volatility = calculateVolatility(values);
  
  return {
    monthlyAverage,
    growthRate,
    volatility
  };
};

// # Helper function to calculate volatility
const calculateVolatility = (values) => {
  if (values.length <= 1) return 0;
  
  // # Calculate average
  const average = values.reduce((sum, value) => sum + (value || 0), 0) / values.length;
  
  // # Calculate variance
  const variance = values.reduce((sum, value) => sum + Math.pow((value || 0) - average, 2), 0) / values.length;
  
  // # Calculate standard deviation
  const stdDev = Math.sqrt(variance);
  
  // # Return coefficient of variation as percentage
  return average > 0 ? (stdDev / average) * 100 : 0;
};

// # Helper function to format currency
const formatCurrency = (value) => {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// # Helper function to format percentage
const formatPercentage = (value) => {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default RevenueAnalytics; 